<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal-content">
      <h2>Gateway Settings - {{ gateway.gw_mac }}</h2>
      
      <div class="settings-section">
        <div class="form-group">
          <label for="deviceName">Device Name:</label>
          <input 
            type="text" 
            id="deviceName" 
            v-model="editedGateway.devicename"
            class="input-field"
            placeholder="Device Name"
          />
        </div>

        <div class="form-group">
          <label for="deviceLocation">Device Location:</label>
          <input 
            type="text" 
            id="deviceLocation" 
            v-model="editedGateway.devicelocation"
            class="input-field"
            placeholder="Device Location"
          />
        </div>

        <div class="form-group">
          <label>Tag Scan Range (RSSI):</label>
          <input 
            type="range" 
            v-model="editedGateway.rssiThreshold" 
            :min="-100" 
            :max="0"
            disabled
            class="slider"
            style="direction: rtl;"
          >
          <div class="rssi-value">Current Value: {{ editedGateway.rssiThreshold }} dBm</div>
          <div class="rssi-range">
            <span>0 dBm</span>
            <span>-100 dBm</span>
          </div>
          <div class="rssi-description">
            RSSI settings can be configured in the administrator page.
          </div>
        </div>
      </div>

      <div class="button-group">
        <button @click="saveSettings" class="save-button" :disabled="isSaving">
          {{ isSaving ? 'Saving...' : 'Save Settings' }}
        </button>
        <button @click="$emit('close')" class="cancel-button">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'EditGateway',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    gateway: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      editedGateway: {
        gw_mac: this.gateway.gw_mac,
        devicename: this.gateway.devicename || '',
        devicelocation: this.gateway.devicelocation || '',
        rssiThreshold: this.gateway.rssiThreshold || -70
      },
      isSaving: false
    }
  },
  async created() {
    await this.loadSettings();
  },
  methods: {
    updateRssiValue(event) {
      this.editedGateway.rssiThreshold = parseInt(event.target.value);
    },
    async loadSettings() {
      try {
        const allSettingsResponse = await fetch('/api/settings/gateway/rssi/all', {
          headers: {
            'Authorization': 'Bearer ' + localStorage.getItem('token')
          }
        });

        if (!allSettingsResponse.ok) {
          throw new Error('Failed to load all RSSI settings');
        }
        const allSettingsData = await allSettingsResponse.json();
        console.log('All gateway settings:', allSettingsData);

        const currentGatewaySetting = allSettingsData.find(
          setting => setting.gw_mac === this.gateway.gw_mac
        );

        if (currentGatewaySetting) {
          this.editedGateway.rssiThreshold = currentGatewaySetting.rssi_filter || -70;
          console.log('Loaded RSSI threshold:', this.editedGateway.rssiThreshold);
        } else {
          this.editedGateway.rssiThreshold = -70;
          console.log('No settings found, using default:', this.editedGateway.rssiThreshold);
        }
      } catch (error) {
        console.error('Failed to load RSSI settings:', error);
        this.editedGateway.rssiThreshold = -70;
      }
    },
    async saveSettings() {
      try {
        this.isSaving = true;
        
        const response = await axios.post('/api/gateway/update', {
          gw_mac: this.gateway.gw_mac,
          devicename: this.editedGateway.devicename,
          devicelocation: this.editedGateway.devicelocation,
          rssiThreshold: this.editedGateway.rssiThreshold
        });

        if (response.data.success) {
          this.$emit('save', { 
            ...this.editedGateway,
            gw_mac: this.gateway.gw_mac 
          });
          this.$emit('close');
        }
      } catch (error) {
        console.error('Failed to save gateway settings:', error);
      } finally {
        this.isSaving = false;
      }
    }
  },
  watch: {
    gateway: {
      handler(newGateway) {
        this.editedGateway = {
          gw_mac: newGateway.gw_mac,
          devicename: newGateway.devicename || '',
          devicelocation: newGateway.devicelocation || '',
          rssiThreshold: newGateway.rssiThreshold || -70
        };
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.settings-page {
  padding: 20px;
}

.settings-container {
  max-width: 800px;
  margin: 0 auto;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.settings-section {
  margin-top: 20px;
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 8px;
  background: #f9f9f9;
}

.slider-container {
  margin: 20px 0;
}

.slider {
  width: 100%;
  height: 8px;
  -webkit-appearance: none;
  appearance: none;
  background: #ddd;
  border-radius: 4px;
  outline: none;
  opacity: 0.7;
  cursor: not-allowed;
  transition: opacity 0.3s;
}

.slider:disabled {
  background: #f0f0f0;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background: #4CAF50;
  border-radius: 50%;
  cursor: not-allowed;
}

.slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #4CAF50;
  border-radius: 50%;
  cursor: not-allowed;
  border: none;
}

.slider-value {
  text-align: center;
  margin-top: 10px;
  font-size: 1.2em;
  font-weight: bold;
  color: #333;
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  color: #666;
  font-size: 0.9em;
}

.description {
  margin-top: 15px;
  color: #666;
  font-size: 0.9em;
  line-height: 1.4;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
}

.modal-content {
  background: white;
  padding: 24px;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: modalFadeIn 0.3s ease;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-content h2 {
  margin: 0 0 20px 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 12px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.input-field {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
  box-sizing: border-box;
}

.input-field:focus {
  border-color: #4CAF50;
  outline: none;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.15);
}

.rssi-value {
  text-align: center;
  margin: 12px 0;
  font-weight: 600;
  color: #333;
  font-size: 16px;
}

.rssi-range {
  display: flex;
  justify-content: space-between;
  margin: 8px 0;
  font-size: 12px;
  color: #666;
}

.rssi-description {
  font-size: 13px;
  color: #666;
  margin-top: 8px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #4CAF50;
}

.button-group {
  display: flex;
  gap: 12px;
  margin-top: 30px;
}

.save-button {
  background-color: #4CAF50;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  flex: 1;
  transition: all 0.3s ease;
}

.save-button:hover:not(:disabled) {
  background-color: #45a049;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(69, 160, 73, 0.3);
}

.save-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.cancel-button {
  background-color: #6c757d;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  flex: 1;
  transition: all 0.3s ease;
}

.cancel-button:hover {
  background-color: #5a6268;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(90, 98, 104, 0.3);
}

/* 반응형 디자인 - 태블릿 (1200px 이하) */
@media screen and (max-width: 1200px) {
  .modal-overlay {
    padding: 16px;
  }
  
  .modal-content {
    padding: 20px;
    max-width: 500px;
  }
  
  .modal-content h2 {
    font-size: 22px;
    margin-bottom: 16px;
  }
  
  .form-group {
    margin-bottom: 16px;
  }
  
  .form-group label {
    font-size: 13px;
    margin-bottom: 6px;
  }
  
  .input-field {
    padding: 10px 12px;
    font-size: 13px;
  }
  
  .rssi-value {
    font-size: 15px;
    margin: 10px 0;
  }
  
  .rssi-description {
    font-size: 12px;
    padding: 10px;
  }
  
  .button-group {
    margin-top: 24px;
    gap: 10px;
  }
  
  .save-button,
  .cancel-button {
    padding: 10px 20px;
    font-size: 13px;
  }
}

/* 반응형 디자인 - 모바일 (768px 이하) */
@media screen and (max-width: 768px) {
  .modal-overlay {
    padding: 12px;
    align-items: flex-start;
    padding-top: 20px;
  }
  
  .modal-content {
    width: 100%;
    max-width: none;
    padding: 16px;
    border-radius: 8px;
    max-height: 85vh;
  }
  
  .modal-content h2 {
    font-size: 20px;
    margin-bottom: 16px;
    text-align: center;
    word-break: break-all;
  }
  
  .settings-section {
    padding: 16px;
    margin-top: 16px;
  }
  
  .form-group {
    margin-bottom: 16px;
    background: white;
    padding: 16px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  .form-group label {
    font-size: 13px;
    margin-bottom: 8px;
    color: #555;
  }
  
  .input-field {
    padding: 12px;
    font-size: 14px;
  }
  
  .slider {
    height: 10px;
  }
  
  .slider::-webkit-slider-thumb {
    width: 24px;
    height: 24px;
  }
  
  .slider::-moz-range-thumb {
    width: 24px;
    height: 24px;
  }
  
  .rssi-value {
    font-size: 16px;
    margin: 12px 0;
  }
  
  .rssi-range {
    font-size: 13px;
    margin: 10px 0;
  }
  
  .rssi-description {
    font-size: 13px;
    padding: 12px;
    margin-top: 12px;
  }
  
  .button-group {
    margin-top: 20px;
    gap: 12px;
    position: sticky;
    bottom: -16px;
    background: white;
    padding: 16px 0 0 0;
    margin-left: -16px;
    margin-right: -16px;
    padding-left: 16px;
    padding-right: 16px;
    border-top: 1px solid #e0e0e0;
  }
  
  .save-button,
  .cancel-button {
    padding: 14px 20px;
    font-size: 14px;
    font-weight: 600;
  }
}

/* 반응형 디자인 - 작은 모바일 (480px 이하) */
@media screen and (max-width: 480px) {
  .modal-overlay {
    padding: 8px;
    padding-top: 16px;
  }
  
  .modal-content {
    padding: 12px;
    border-radius: 6px;
    max-height: 90vh;
  }
  
  .modal-content h2 {
    font-size: 18px;
    margin-bottom: 12px;
    padding-bottom: 8px;
  }
  
  .settings-section {
    padding: 12px;
    margin-top: 12px;
  }
  
  .form-group {
    margin-bottom: 12px;
    padding: 12px;
  }
  
  .form-group label {
    font-size: 12px;
    margin-bottom: 6px;
  }
  
  .input-field {
    padding: 10px;
    font-size: 13px;
  }
  
  .rssi-value {
    font-size: 15px;
    margin: 10px 0;
  }
  
  .rssi-range {
    font-size: 12px;
  }
  
  .rssi-description {
    font-size: 12px;
    padding: 10px;
  }
  
  .button-group {
    margin-top: 16px;
    gap: 8px;
    flex-direction: column;
    padding-left: 12px;
    padding-right: 12px;
    margin-left: -12px;
    margin-right: -12px;
  }
  
  .save-button,
  .cancel-button {
    padding: 12px 16px;
    font-size: 13px;
    width: 100%;
  }
}

/* 큰 화면에서의 최적화 (1600px 이상) */
@media screen and (min-width: 1600px) {
  .modal-overlay {
    padding: 24px;
  }
  
  .modal-content {
    padding: 28px;
    max-width: 700px;
  }
  
  .modal-content h2 {
    font-size: 26px;
    margin-bottom: 24px;
  }
  
  .settings-section {
    padding: 24px;
    margin-top: 24px;
  }
  
  .form-group {
    margin-bottom: 24px;
  }
  
  .form-group label {
    font-size: 15px;
    margin-bottom: 10px;
  }
  
  .input-field {
    padding: 14px 16px;
    font-size: 15px;
  }
  
  .rssi-value {
    font-size: 18px;
    margin: 16px 0;
  }
  
  .rssi-range {
    font-size: 14px;
    margin: 12px 0;
  }
  
  .rssi-description {
    font-size: 14px;
    padding: 16px;
  }
  
  .button-group {
    margin-top: 36px;
    gap: 16px;
  }
  
  .save-button,
  .cancel-button {
    padding: 14px 28px;
    font-size: 15px;
  }
}

/* 랜드스케이프 모드 최적화 */
@media screen and (max-width: 768px) and (orientation: landscape) {
  .modal-overlay {
    padding: 8px;
    align-items: center;
  }
  
  .modal-content {
    max-height: 95vh;
    padding: 12px;
  }
  
  .modal-content h2 {
    font-size: 16px;
    margin-bottom: 12px;
    padding-bottom: 6px;
  }
  
  .settings-section {
    padding: 12px;
    margin-top: 12px;
  }
  
  .form-group {
    margin-bottom: 10px;
    padding: 10px;
  }
  
  .form-group label {
    font-size: 11px;
    margin-bottom: 4px;
  }
  
  .input-field {
    padding: 8px;
    font-size: 12px;
  }
  
  .rssi-value {
    font-size: 14px;
    margin: 8px 0;
  }
  
  .rssi-description {
    font-size: 11px;
    padding: 8px;
  }
  
  .button-group {
    margin-top: 12px;
    gap: 8px;
    flex-direction: row;
  }
  
  .save-button,
  .cancel-button {
    padding: 8px 12px;
    font-size: 12px;
  }
}

/* 접근성 개선 */
@media (prefers-reduced-motion: reduce) {
  .modal-content {
    animation: none;
  }
  
  .save-button,
  .cancel-button,
  .input-field {
    transition: none;
  }
  
  .save-button:hover,
  .cancel-button:hover {
    transform: none;
  }
}

/* 다크모드 대응 */
@media (prefers-color-scheme: dark) {
  .modal-content {
    background: #2d2d2d;
    color: #ffffff;
  }
  
  .modal-content h2 {
    color: #ffffff;
    border-bottom-color: #404040;
  }
  
  .settings-section {
    background: #333333;
    border-color: #404040;
  }
  
  .form-group {
    background: #333333;
  }
  
  .form-group label {
    color: #cccccc;
  }
  
  .input-field {
    background-color: #404040;
    border-color: #555555;
    color: #ffffff;
  }
  
  .input-field:focus {
    border-color: #5caf60;
    box-shadow: 0 4px 12px rgba(92, 175, 96, 0.15);
  }
  
  .rssi-value {
    color: #ffffff;
  }
  
  .rssi-range {
    color: #cccccc;
  }
  
  .rssi-description {
    background: #404040;
    color: #cccccc;
    border-left-color: #5caf60;
  }
  
  .button-group {
    background: #2d2d2d;
    border-top-color: #404040;
  }
  
  .slider {
    background: #555555;
  }
  
  .slider:disabled {
    background: #333333;
  }
}
</style>
  